import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/assignments";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
} from "@/components/ui/dropdown-menu";
import { Bell, CheckCheck, Inbox, Loader2 } from "lucide-react";

export default function NotificationBell() {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  const loadCount = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread_count");
      setCount(data.count || 0);
    } catch (e) { /* ignore */ }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications", { params: { limit: 30 } });
      setItems(data);
    } catch (e) { setItems([]); } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    loadCount();
    timer.current = setInterval(loadCount, 30000);
    return () => clearInterval(timer.current);
  }, [loadCount]);

  useEffect(() => { if (open) loadList(); }, [open, loadList]);

  const openItem = async (n) => {
    setOpen(false);
    try {
      await api.post(`/notifications/${n.id}/read`);
    } catch (e) { /* ignore */ }
    loadCount();
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    try { await api.post("/notifications/read_all"); } catch (e) { /* ignore */ }
    setCount(0);
    loadList();
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className="relative flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-brand"
          data-testid="notification-bell"
          title="Notifiche"
        >
          <Bell className="h-5 w-5" />
          {count > 0 && (
            <span
              data-testid="notification-badge"
              className="absolute -right-0.5 -top-0.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold text-white"
            >
              {count > 99 ? "99+" : count}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[360px] bg-white p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between border-b border-border px-3 py-2.5">
          <span className="text-sm font-semibold text-slate-800">Notifiche</span>
          <button onClick={markAll} data-testid="notification-mark-all"
            className="inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline">
            <CheckCheck className="h-3.5 w-3.5" /> Segna tutte lette
          </button>
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-brand" /></div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-10 text-slate-400">
              <Inbox className="h-7 w-7" /> <span className="text-sm">Nessuna notifica</span>
            </div>
          ) : items.map((n) => (
            <button
              key={n.id}
              onClick={() => openItem(n)}
              data-testid={`notification-item-${n.id}`}
              className={`flex w-full flex-col items-start gap-0.5 border-b border-slate-50 px-3 py-2.5 text-left transition-colors hover:bg-brand/5 ${n.status === "nuova" ? "bg-brand/[0.04]" : ""}`}
            >
              <div className="flex w-full items-center gap-2">
                {n.status === "nuova" && <span className="h-2 w-2 shrink-0 rounded-full bg-danger" />}
                <span className="truncate text-sm font-semibold text-slate-800">{n.title}</span>
              </div>
              <span className="text-xs text-slate-500">{n.body}</span>
              <span className="text-[11px] text-slate-400">{fmtDateTime(n.created_at)}</span>
            </button>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
